export type TimezoneEntry = { iana: string; label: string };

type SlotLabelArg = { date: Date; text: string };

export const getTimezoneCity = (timezone: string): string => {
  const parts = timezone.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
};

export const initialTimezones = (
  _referenceTimezone: string
): TimezoneEntry[] => [];

// Storage migration. Before the system timezone stopped being persisted, it
// was seeded into storage on the first visit and kept the trailing
// (reference) spot, since added zones are inserted in front. Such an entry
// is provably that seed — not a user choice — while it still matches the
// machine's current timezone and keeps its auto-generated label: the
// reference zone cannot be picked from the add menu, and renaming marks an
// entry as deliberately owned. Once the user travels, a leftover seed
// becomes indistinguishable from a deliberate pin and is kept; the label
// chips let them delete it manually.
export const withoutSeededTimezone = (
  timezones: TimezoneEntry[],
  referenceTimezone: string
): TimezoneEntry[] => {
  const last = timezones[timezones.length - 1];
  return last !== undefined &&
    last.iana === referenceTimezone &&
    last.label === getTimezoneCity(referenceTimezone)
    ? timezones.slice(0, -1)
    : timezones;
};

// The timezones to display. The calendar natively owns the first spot: the
// time column beside the event grid, labeled by FullCalendar itself in the
// system timezone. That spot is materialized here as a virtual trailing
// entry (the innermost column is the last one rendered) rather than read
// from storage, and the stored user-created zones stack outward before it.
// A stored zone that already matches the reference (a pre-fix persisted
// entry, or a deliberate pin) takes over the spot instead of duplicating it.
export const visibleTimezones = (
  timezones: TimezoneEntry[],
  referenceTimezone: string
): TimezoneEntry[] =>
  timezones.some((timezone) => timezone.iana === referenceTimezone)
    ? timezones
    : [
        ...timezones,
        { iana: referenceTimezone, label: getTimezoneCity(referenceTimezone) },
      ];

export const formatTimeInTimezone = (
  slotDate: Date,
  timezone: string,
  is24h: boolean,
  now = new Date()
): string => {
  try {
    const date = new Date(now);
    date.setHours(slotDate.getHours(), slotDate.getMinutes(), 0, 0);
    return new Intl.DateTimeFormat(undefined, {
      hour: is24h ? "2-digit" : "numeric",
      minute: is24h ? "2-digit" : undefined,
      hour12: !is24h,
      timeZone: timezone,
    }).format(date);
  } catch {
    return "";
  }
};

export const slotLabelContent = (
  arg: SlotLabelArg,
  timezones: TimezoneEntry[],
  referenceTimezone: string,
  is24h: boolean,
  now = new Date()
): string | { html: string } => {
  const visible = visibleTimezones(timezones, referenceTimezone);
  // A single visible zone is necessarily the reference zone itself (the
  // virtual entry, or a stored zone matching it), so FullCalendar's own
  // label is already correct.
  if (visible.length <= 1) return arg.text;
  const spans = visible.map((timezone, index) => {
    const time =
      timezone.iana === referenceTimezone
        ? arg.text
        : formatTimeInTimezone(arg.date, timezone.iana, is24h, now);
    const cls =
      index === visible.length - 1
        ? "fc-dual-time-primary"
        : "fc-dual-time-secondary";
    return `<span class="${cls}">${time}</span>`;
  });
  return { html: `<span class="fc-dual-time">${spans.join("")}</span>` };
};
